import React from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// FONTE ÚNICA DE VERDADE (single source of truth) das telas e permissões.
//
// Toda tela do sistema deve estar declarada aqui — seja como item do menu lateral
// (`menuItems`) ou como página acessível fora do menu (`extraPages`).
//
// Ao adicionar ou remover uma tela AQUI, tudo se atualiza automaticamente:
//   • Sidebar (menu lateral)                → importa `menuItems`
//   • App.js (guarda de rota por permissão) → importa `PAGE_PERMISSIONS`
//   • Perfis de Acesso (tela de permissões) → importa `getPermissionCatalog()`
//
// REGRA: nunca liste permissões/telas manualmente em outro arquivo. Basta
// declarar a tela aqui e o menu de criação de Perfil refletirá sozinho.
// ─────────────────────────────────────────────────────────────────────────────

// Itens do menu lateral (com ícones). `isExpandable`+`submenus` = grupo.
export const menuItems = [
  {
    id: 'dashboard', label: 'Dashboard', permission: 'view_dashboard',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
  },
  {
    id: 'projects', label: 'NexoProject', permission: 'view_dashboard',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
  },
  {
    id: 'call-copilot', label: 'Copiloto de Conversas', permission: 'view_deals', isExpandable: true,
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h8M8 14h5m7-2a8 8 0 01-8 8H7l-4 2 1.5-4A8 8 0 1119 12z" /></svg>,
    submenus: [
      { id: 'call-copilot', label: 'Iniciar conversa', permission: 'view_deals' },
      { id: 'call-playbooks', label: 'Objetivos e playbooks', permission: 'view_deals' },
      { id: 'call-responses', label: 'Respostas fixas', permission: 'view_deals' },
      { id: 'call-feedback', label: 'Feedback de conversas', permission: 'view_deals' },
      { id: 'call-analytics', label: 'Analytics de conversas', permission: 'view_deals' },
    ]
  },
  {
    id: 'crm', label: 'Leads Journey', permission: 'view_leads', isExpandable: true,
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
    submenus: [
      { id: 'leads',                label: 'Leads',                 permission: 'view_leads' },
      { id: 'pipeline',             label: 'Pipeline (Kanban)',      permission: 'view_leads' },
      { id: 'deals',                label: 'Negócios',               permission: 'view_dashboard' },
      { id: 'agenda',               label: 'Agenda Comercial',        permission: 'view_deals' },
      { id: 'pipeline-intelligence',label: 'Inteligência do Pipeline', permission: 'view_deals' },
      { id: 'funnels',              label: 'Config. Funis',          permission: 'view_funnels' },
    ]
  },
  {
    id: 'integrations', label: 'Integrações', permission: 'view_integrations', isExpandable: true,
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>,
    submenus: [
      { id: 'integrations', label: 'Integrações Gerais', permission: 'view_integrations' },
      { id: 'social-integrations', label: 'Redes Sociais', permission: 'view_integrations' },
    ]
  },
  {
    id: 'api-configurations', label: 'Config. de APIs', permission: 'manage_system',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
  },
  {
    id: 'prospecting', label: 'Prospecções', permission: 'view_prospecting', isExpandable: true,
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>,
    submenus: [
      { id: 'email-finder', label: 'Busca de Emails', permission: 'view_prospecting' },
      { id: 'linkedin-campaigns', label: 'LinkedIn', permission: 'view_prospecting' },
      { id: 'lead-extraction', label: 'Extração de Leads', permission: 'view_prospecting' },
    ]
  },
  {
    id: 'bulk-upload', label: 'Upload em Massa', permission: 'bulk_upload',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 12l3-3m0 0l3 3m-3-3v9" /></svg>
  },
  {
    id: 'unique-channel', label: 'UniqueChannel', permission: 'view_dashboard',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
  },
  {
    id: 'nexo-intelligence', label: 'SDR NexoIntelligence', permission: 'view_dashboard',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" /></svg>
  },
  {
    id: 'nexohub', label: 'NexoHub', permission: 'view_dashboard',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>
  },
  {
    id: 'nexobid', label: 'NexoBid', permission: 'view_dashboard',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
  },
  {
    id: 'users', label: 'Usuários', permission: 'manage_users',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
  },
  {
    id: 'profiles', label: 'Perfis de Acesso', permission: 'manage_profiles',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
  },
  {
    id: 'email-config', label: 'Config. E-mail', permission: 'manage_system',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
  },
  {
    id: 'companies', label: 'Empresas', permission: 'view_leads',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
  },
  {
    id: 'billing', label: 'Faturamento', permission: 'view_dashboard',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
  },
  {
    id: 'tutorial', label: 'Tutorial', permission: null,
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C20.168 18.477 18.582 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
  },
];

// Telas acessíveis que NÃO aparecem como item próprio no menu lateral
// (abertas por botões/ações internas). Declare aqui para que também entrem
// na guarda de rota e no catálogo de permissões de Perfil.
export const extraPages = {
  activities:        { permission: 'view_dashboard', label: 'Atividades' },
  creation:          { permission: 'view_creation',  label: 'Criação de Conteúdo' },
  proposals:         { permission: 'view_proposals', label: 'Propostas' },
  quotes:            { permission: 'view_quotes',    label: 'Orçamentos' },
  tasks:             { permission: 'view_tasks',     label: 'Tarefas' },
  'email-marketing': { permission: 'view_dashboard', label: 'E-mail Marketing' },
};

// Metadados amigáveis por permissão para a tela de Perfis. Se uma permissão
// nova surgir sem estar aqui, o catálogo usa um rótulo automático (fallback).
export const PERMISSION_META = {
  view_dashboard:   { label: 'Painel e Módulos Gerais', description: 'Dashboard e módulos gerais do sistema', group: 'Geral' },
  view_leads:       { label: 'Leads e Empresas',        description: 'Visualizar leads, pipeline e empresas', group: 'Leads & Vendas' },
  view_deals:       { label: 'Inteligência do Pipeline', description: 'Análise inteligente de negócios', group: 'Leads & Vendas' },
  view_funnels:     { label: 'Configuração de Funis',   description: 'Gerenciar funis de venda', group: 'Leads & Vendas' },
  view_proposals:   { label: 'Propostas',               description: 'Acessar propostas comerciais', group: 'Leads & Vendas' },
  view_quotes:      { label: 'Orçamentos',              description: 'Acessar orçamentos', group: 'Leads & Vendas' },
  view_integrations:{ label: 'Integrações',             description: 'Integrações e redes sociais', group: 'Integrações & Prospecção' },
  view_prospecting: { label: 'Prospecção',              description: 'Busca de e-mails, LinkedIn e extração de leads', group: 'Integrações & Prospecção' },
  view_creation:    { label: 'Criação de Conteúdo',     description: 'Ferramentas de criação de conteúdo', group: 'Conteúdo' },
  view_tasks:       { label: 'Tarefas',                 description: 'Acessar e acompanhar tarefas', group: 'Produtividade' },
  bulk_upload:      { label: 'Upload em Massa',         description: 'Importar dados em massa', group: 'Ferramentas' },
  manage_system:    { label: 'Configurações do Sistema', description: 'Config. de APIs e de e-mail', group: 'Administração' },
  manage_users:     { label: 'Gerenciar Usuários',      description: 'Criar e gerenciar contas de usuário', group: 'Administração' },
  manage_profiles:  { label: 'Gerenciar Perfis',        description: 'Criar e gerenciar perfis de acesso', group: 'Administração' },
  // `standalone`: permissão sem tela própria no menu — entra no catálogo mesmo assim.
  access_api:       { label: 'Acesso à API Pública',    description: 'Gerar chaves de API e usar a API pública (/api/v1)', group: 'Administração', standalone: true },
};

// Ordem de exibição dos grupos na tela de Perfis.
const GROUP_ORDER = [
  'Geral',
  'Leads & Vendas',
  'Integrações & Prospecção',
  'Conteúdo',
  'Produtividade',
  'Ferramentas',
  'Administração',
  'Outros',
];

// Todas as telas do sistema (menu + extraPages) como lista plana {id, label, permission}.
function getAllScreens() {
  const screens = [];
  menuItems.forEach((item) => {
    if (item.submenus) {
      // grupos expansíveis: as telas reais são os submenus
      item.submenus.forEach((s) => screens.push({ id: s.id, label: s.label, permission: s.permission }));
    } else {
      screens.push({ id: item.id, label: item.label, permission: item.permission });
    }
  });
  Object.entries(extraPages).forEach(([id, v]) => {
    screens.push({ id, label: v.label, permission: v.permission });
  });
  return screens;
}

// Mapa página -> permissão (guarda de rota). Derivado das telas. null = público.
export const PAGE_PERMISSIONS = getAllScreens().reduce((acc, s) => {
  acc[s.id] = s.permission ?? null;
  return acc;
}, {});

// Catálogo de permissões agrupado, para a tela de Perfis de Acesso.
// Deriva automaticamente das telas reais: cada permissão lista as telas que libera.
export function getPermissionCatalog() {
  const byPerm = new Map();
  getAllScreens().forEach((screen) => {
    const perm = screen.permission;
    if (!perm) return; // telas públicas não geram permissão
    if (!byPerm.has(perm)) {
      const meta = PERMISSION_META[perm] || {};
      byPerm.set(perm, {
        id: perm,
        label: meta.label || screen.label,
        description: meta.description || '',
        group: meta.group || 'Outros',
        screens: [],
      });
    }
    const entry = byPerm.get(perm);
    if (!entry.screens.includes(screen.label)) entry.screens.push(screen.label);
  });

  // Permissões sem tela no menu (ex.: access_api) também precisam aparecer para poderem ser concedidas.
  Object.entries(PERMISSION_META).forEach(([perm, meta]) => {
    if (meta.standalone && !byPerm.has(perm)) {
      byPerm.set(perm, {
        id: perm,
        label: meta.label,
        description: meta.description || '',
        group: meta.group || 'Outros',
        screens: [],
      });
    }
  });

  // Agrupa e ordena
  const groupsMap = new Map();
  byPerm.forEach((entry) => {
    if (!groupsMap.has(entry.group)) groupsMap.set(entry.group, []);
    groupsMap.get(entry.group).push(entry);
  });

  return Array.from(groupsMap.entries())
    .sort((a, b) => {
      const ia = GROUP_ORDER.indexOf(a[0]);
      const ib = GROUP_ORDER.indexOf(b[0]);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    })
    .map(([group, permissions]) => ({ group, permissions }));
}

// Lista plana de todas as permissões conhecidas (ids).
export function getKnownPermissionIds() {
  return getPermissionCatalog().flatMap((g) => g.permissions.map((p) => p.id));
}

// Rótulo amigável de uma permissão (para badges etc.).
export function getPermissionLabel(permissionId) {
  if (typeof permissionId === 'string' && permissionId.startsWith(ACCESS_PREFIX)) {
    return getAccessLabel(permissionId);
  }
  return PERMISSION_META[permissionId]?.label || permissionId;
}

// ─────────────────────────────────────────────────────────────────────────────
// MODELO POR TELA (access_*) — permite conceder/negar cada tela individualmente.
//
// Cada tela (item de menu, submenu ou extraPage) recebe um id de acesso próprio:
//   accessId = `access_<idDaTela>`
//
// Um perfil "novo" guarda esses accessIds + as permissões-base (backend) que as
// telas selecionadas precisam para funcionar. Perfis "antigos" (sem nenhum
// access_*) continuam funcionando pelo modelo legado (permissão-base). O admin
// sempre tem acesso total.
// ─────────────────────────────────────────────────────────────────────────────

export const ACCESS_PREFIX = 'access_';
export const accessIdFor = (pageId) => `${ACCESS_PREFIX}${pageId}`;

// Telas que podem ser controladas individualmente (exclui telas públicas).
function getControllableScreens() {
  return getAllScreens().filter((s) => s.permission != null);
}

// Todos os accessIds gerenciáveis pelo seletor de telas.
export function getAllAccessIds() {
  return getControllableScreens().map((s) => accessIdFor(s.id));
}

// Conjunto de permissões-base (backend) que o seletor de telas gerencia.
export function getManagedBasePermissions() {
  return Array.from(new Set(getControllableScreens().map((s) => s.permission)));
}

// Dado os accessIds selecionados, retorna as permissões-base (backend) necessárias.
export function getBasePermissionsForAccess(accessIds) {
  const selected = new Set(accessIds);
  const perms = new Set();
  getControllableScreens().forEach((s) => {
    if (selected.has(accessIdFor(s.id)) && s.permission) perms.add(s.permission);
  });
  return Array.from(perms);
}

// Um perfil usa o modelo por tela se possui ao menos um accessId.
export function profileUsesScreenModel(permissions = []) {
  return permissions.some((p) => typeof p === 'string' && p.startsWith(ACCESS_PREFIX));
}

// Gating central de acesso a uma página (usado por Sidebar e App).
export function canAccessPage(pageId, user) {
  if (user?.profile === 'admin' || user?.role === 'admin') return true;
  const perms = user?.permissions || [];
  const base = PAGE_PERMISSIONS[pageId];
  // Página não catalogada (ex: creation/activities/tasks/quotes — sub-telas de
  // ação alcançadas a partir de telas já protegidas) ou pública (base null):
  // passthrough sem gate individual.
  if (base === null || base === undefined) return true;
  if (profileUsesScreenModel(perms)) return perms.includes(accessIdFor(pageId));
  return perms.includes(base); // modelo legado
}

// Rótulo amigável de um accessId (usa o label da tela correspondente).
export function getAccessLabel(accessId) {
  const pageId = accessId.startsWith(ACCESS_PREFIX) ? accessId.slice(ACCESS_PREFIX.length) : accessId;
  const screen = getAllScreens().find((s) => s.id === pageId);
  return screen?.label || pageId;
}

// Árvore de navegação para a tela de Perfis: menus principais expansíveis com
// seus submenus, mais um grupo "Outras Telas" para as extraPages.
export function getScreenTree() {
  const nodes = [];
  menuItems.forEach((item) => {
    if (item.submenus) {
      const children = item.submenus
        .filter((s) => s.permission != null)
        .map((s) => ({ id: s.id, label: s.label, accessId: accessIdFor(s.id) }));
      if (children.length) nodes.push({ id: item.id, label: item.label, type: 'group', children });
    } else if (item.permission != null) {
      nodes.push({ id: item.id, label: item.label, type: 'item', accessId: accessIdFor(item.id) });
    }
  });
  const extras = Object.entries(extraPages)
    .filter(([, v]) => v.permission != null)
    .map(([id, v]) => ({ id, label: v.label, accessId: accessIdFor(id) }));
  if (extras.length) nodes.push({ id: '__extras__', label: 'Outras Telas', type: 'group', children: extras });
  return nodes;
}

// Converte as permissões salvas de um perfil na lista de accessIds selecionados
// para exibir no seletor de telas. Perfis novos usam access_* diretamente;
// perfis legados são inferidos pelas permissões-base.
export function deriveAccessIdsFromPermissions(permissions = []) {
  if (profileUsesScreenModel(permissions)) {
    const all = new Set(getAllAccessIds());
    return Array.from(new Set(permissions.filter((p) => all.has(p))));
  }
  const perms = new Set(permissions);
  return getControllableScreens()
    .filter((s) => perms.has(s.permission))
    .map((s) => accessIdFor(s.id));
}

// Expande os accessIds selecionados nas permissões finais a salvar no perfil:
// accessIds + permissões-base necessárias + extras não gerenciadas preservadas.
export function expandAccessToPermissions(accessIds = [], originalPermissions = []) {
  const base = getBasePermissionsForAccess(accessIds);
  const managed = new Set(getManagedBasePermissions());
  const extras = originalPermissions.filter(
    (p) => typeof p === 'string' && !p.startsWith(ACCESS_PREFIX) && !managed.has(p)
  );
  return Array.from(new Set([...accessIds, ...base, ...extras]));
}
